import React, { useState, useMemo } from 'react';
import { Box, Typography, Fab, Chip } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import * as Icons from '../constants/icons';
import { PAGE_FILL_SX, TABLE_FILL_SX } from '../constants/layout';
import { useAiPlansTable } from '../hooks/useAiPlansTable';
import StandardDataTable, { Column } from '../components/StandardDataTable';
import AiPlanDetail, { AiPlanRecord } from '../components/AiPlanDetail';
import { useNotification } from '../providers/NotificationProvider';

export default function AiPlansManagement() {
  const notify = useNotification();
  const {
    data,
    totalCount,
    loading,
    page,
    pageSize,
    setPage,
    setPageSize,
    handleSort,
    refresh,
  } = useAiPlansTable();

  const [selectedPlan, setSelectedPlan] = useState<AiPlanRecord | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const columns = useMemo<Column[]>(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        width: 80,
        sticky: true,
        renderCell: (row) => (
          <span
            style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => {
              setSelectedPlan(row as AiPlanRecord);
              setIsDetailOpen(true);
            }}
          >
            #{row.id}
          </span>
        ),
      },
      {
        field: 'planned_date',
        headerName: '計画策定日',
        width: 120,
      },
      {
        field: 'author',
        headerName: '作成者',
        width: 200,
        renderCell: (row) => {
          const isAi = String(row.author || '').startsWith('AI');
          return (
            <Chip
              label={row.author}
              size="small"
              variant="outlined"
              color={isAi ? 'primary' : 'default'}
              icon={isAi ? <Icons.Analytics fontSize="small" /> : <Icons.Person fontSize="small" />}
            />
          );
        },
      },
      {
        field: 'goal',
        headerName: '達成目的',
        width: 360,
      },
      {
        field: 'file_paths',
        headerName: '対象ファイル',
        width: 320,
        renderCell: (row) => {
          const paths: string[] = row.file_paths || [];
          if (paths.length === 0) return <span style={{ color: '#9e9e9e' }}>—</span>;
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {paths.slice(0, 2).map((p, i) => (
                <Chip key={i} label={p} size="small" variant="outlined" />
              ))}
              {paths.length > 2 && <Chip label={`+${paths.length - 2}`} size="small" />}
            </Box>
          );
        },
      },
      {
        field: 'conversation_id',
        headerName: 'AI会話ID',
        width: 180,
      },
      {
        field: 'updated_at',
        headerName: '最終更新日',
        width: 160,
        valueFormatter: (value) => {
          if (!value) return '';
          return String(value).substring(0, 16).replace('T', ' ');
        },
      },
    ],
    []
  );

  return (
    <Box sx={{ ...PAGE_FILL_SX, py: 3, width: '95%', margin: '0 auto' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: blueGrey[500],
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Icons.Description /> AI実装プラン管理
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Fab
            variant="extended"
            size="small"
            onClick={refresh}
            sx={{
              height: '32px',
              px: 2,
              bgcolor: '#eeeeee',
              color: '#444444',
              boxShadow: 1,
              '&:hover': { bgcolor: '#607d8b', color: '#fff' },
            }}
          >
            <Icons.Refresh sx={{ mr: 1, fontSize: 18 }} />
            再読込
          </Fab>

          <Fab
            variant="extended"
            size="small"
            color="primary"
            onClick={() => {
              // TODO: 新規プラン作成フォームを開く
              notify.info('新規プラン作成は未実装です');
            }}
            sx={{ height: '32px', px: 2, boxShadow: 1 }}
          >
            <Icons.Add sx={{ mr: 1, fontSize: 18 }} />
            新規プラン
          </Fab>
        </Box>
      </Box>

      <Box sx={TABLE_FILL_SX}>
        <StandardDataTable
          rows={data}
          loading={loading}
          loadingMessage="AI実装プランを取得中"
          totalCount={totalCount}
          page={page}
          pageSize={pageSize}
          columns={columns}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSortChange={handleSort}
        />
      </Box>

      <AiPlanDetail
        open={isDetailOpen}
        plan={selectedPlan}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedPlan(null);
        }}
      />
    </Box>
  );
}
